import { useEffect, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/QuestionTechnologySlice";
import { RootState } from "src/redux/store";
import { useDeleteQuestionCategoryMutation, useGetAllQuestionCategoryWithPaginationQuery } from "src/services/QuestionCategoryServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddJobFormWrapper from "../Add/AddQuestionCategoryFormWrapper";
import EditJobFormWrapper from "../Edit/EditQuestionCategoryFormWrapper";
import QuestionCategoryListing from "./QuestionCategoryListing";

const QuestionCategoryListingWrapper = () => {
  const dispatch = useDispatch();

  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [deleteJob] = useDeleteQuestionCategoryMutation();

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.questionCategory);

  const { data, isLoading, isFetching } = useGetAllQuestionCategoryWithPaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "categoryName"
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (jobId: string) => {
    return [
      {
        label: (
          <div className="flex items-center gap-2 text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setSelectedId(jobId);
          setIsOpenEditForm(true);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.QUESTION_CATEGORY,
      },
      {
        label: (
          <div className="flex items-center gap-2 font-semibold text-red-600">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Category ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                deleteJob({ id: jobId }).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.QUESTION_CATEGORY,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "categoryName",
      headerName: "Category",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[120x]",
      renderCell: (row: any) => (
        <div className="capitalize"> {row?.categoryName} </div>
      ),
    },

    {
      field: "action",
      headerName: "",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  return (
    <div>
      <QuestionCategoryListing
        rows={items}
        columns={columns}
        getActionOptions={getActionOptions}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddFrom ? (
        <AddJobFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditForm ? (
        <EditJobFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          categoryId={selectedId}
        />
      ) : null}
    </div>
  );
};

export default QuestionCategoryListingWrapper;
